<template>
    <div>
        <ul class="uk-subnav uk-subnav-line">
            <li :class="{'uk-active': !state.status}"><a href @click.prevent="filter_by_field('', 'status')">All</a></li>
            <li
                v-for="status in available_project_status"
                :key="status.id"
                :class="{'uk-active': (state.status === (status.code_constant||status.id)) }"
            >
                <a href @click.prevent="filter_by_field(status.code_constant||status.id, 'status')">{{ status.name }} ({{ status.usage_count || 0 }})</a>
            </li>
        </ul>

        <data-table
            id="prospective-projects"
            :columns="visible_columns"
            :rows="filtered_rows"
            :search="state.search"
            :total="state.total"
            :found="state.found"
            :current-page="state.page"
            :total-pages="nb_pages"
            :searchable="false"
            table-class="lam-records-list"
            :row-class-provider="row_classes"
            empty-title="No projects found"
            :export-entity="entity"
            :export-query="state"
            :export-fields="export_fields"
            @sort="sort_by"
            @search="search"
            @page="goto_page"
            @click="record_edit(entity, $event.id)"
        >
            <template v-slot:controls>
                <div>
                    <base-button
                        flavor="link"
                        @click.prevent="toggle_filters(true)"
                    >
                        <span v-html="applied_filters"></span>
                    </base-button>
                    <base-button
                        v-if="!!filter_description"
                        flavor="link"
                        @click.prevent="clear_filters"
                        class="uk-margin-left"
                    >
                        <open-icon glyph="close" />
                    </base-button>
                    <base-button
                        v-if="!!templates.length"
                        flavor="link"
                        @click.native="picking_template=!picking_template"
                    >
                        <open-icon glyph="list-ul" />
                    </base-button>
                </div>
            </template>
            <template
                v-slot:summary="{ row }"
            >
                <h6>{{ row.name }}</h6>
            </template>
            <template
                v-slot:tbody="{ row, field }"
            >
                <router-link
                    v-if="field === 'name'"
                    :to="{ name: 'project.edit', params: { id: row.id } }"
                >
                    {{ row.name }}
                </router-link>
                <span v-else-if="field === 'sortable_number'">{{ row.sortable_number | lam.project-number }}</span>
                <span v-else-if="field.match(/computed_(prospective_fee|fee|spent)/i)">$&nbsp;{{ row[field] | nibnut.number("0,0") }}</span>
                <div v-else-if="field === 'computed_starts_at_as_string'">
                    <span
                        v-if="!row.computed_starts_at_as_string || !row.computed_ends_at_as_string"
                        class="uk-text-danger"
                    >
                        <open-icon glyph="calendar-times-o" />
                    </span>
                    <span v-else>{{ row[field] | nibnut.date("MM/YYYY") }}</span>
                </div>
                <div v-else-if="field === 'computed_last_communication_at_as_string'">
                    <span
                        v-if="!row[field]"
                        class="uk-text-danger"
                    >
                        <open-icon glyph="calendar-times-o" />
                    </span>
                    <span v-else>{{ row[field] | nibnut.date("MM/DD/YY") }}</span>
                </div>
                <div v-else-if="field === 'proposal_sent_at_as_string'">
                    <span
                        v-if="!row[field]"
                        class="uk-text-danger"
                    >
                        <open-icon glyph="calendar-times-o" />
                    </span>
                    <span v-else>{{ row[field] | nibnut.date("MM/DD/YY") }}</span>
                </div>
                <span v-else-if="field === 'days_since_received'">
                    <span v-if="row[field] >= 60">{{ (row[field] / 30) | nibnut.number("0,0") }}&nbsp;months</span>
                    <span v-else>{{ row[field] | nibnut.number("0,0") }}&nbsp;days</span>
                </span>
                <span v-else-if="field.match(/(completed|computed_starts)_at_as_string/)">{{ row[field] | nibnut.date("MM/YYYY") }}</span>
                <span v-else-if="field === 'consider_for_marketing'">
                    <small v-if="!!row[field]" class="uk-text-warning"><open-icon glyph="warning" title="This project should be considered for marketing purposes" /> Should be considered</small>
                </span>
                <span v-else>{{ row[field] }}</span>
            </template>
        </data-table>

        <projects-list-filter-dialog
            :show.sync="showing_filters"
            :state="state"
            :entity="entity"
            :prospective="prospective"
            :stateUi="state_ui"
            :available-project-status="available_project_status"
            :size-unit-html="size_unit_html"
            @filter="filter_by_field"
            @pick-firm="pick_firm"
            @units-changed="units_changed"
        >
            <template v-slot:pick-template-button>
                <span v-if="!!templates.length"  class="uk-margin-left">
                    <base-button size="mini" @click.native="picking_template=!picking_template">
                        <open-icon glyph="list-ul" />
                    </base-button>
                </span>
            </template>
            <template v-slot:templates-controls>
                <templates-controls
                    :template="current_template"
                    :entity="entity"
                    category="filters"
                    :savable="template_savable"
                    :dirty="template_dirty"
                    :clone="template_to_clone"
                    @save="template_editor_save"
                    @stop-editing="template_editor_close"
                />
            </template>
        </projects-list-filter-dialog>
        <template-picker
            :template="current_template"
            :entity="entity"
            :active="picking_template"
            title="Saved Filters"
            category="filters"
            @close="picking_template=false"
            @clone="template_clone"
            @load="template_load"
        />
    </div>
</template>

<script type="text/javascript">
import { mapGetters } from "vuex"

import is_projects_list from "./IsProjectsList"

export default {
    mixins: [is_projects_list],
    computed: {
        ...mapGetters(["entity_records"]),
        state_identifier () {
            return "project.list"
        },
        visible_columns () {
            const columns = {}

            // Number, project name, location, architect, PIC, PM, Fee, Fee spent, Should be onsidered for makreting…, completion date
            if(this.state.status === "completed") {
                columns.sortable_number = this.columns.sortable_number
                columns.name = this.columns.name
                columns.city_state = this.columns.city_state
                columns.client_name = this.columns.client_name
                columns.pic_initials = this.columns.pic_initials
                columns.pm_initials = this.columns.pm_initials
                columns.computed_fee = this.columns.computed_fee
                columns.computed_spent = this.columns.computed_spent
                columns.consider_for_marketing = this.columns.consider_for_marketing
                columns.completed_at_as_string = this.columns.completed_at_as_string
            } else {
                columns.sortable_number = this.columns.sortable_number
                columns.name = this.columns.name
                columns.city_state = this.columns.city_state
                columns.client_name = this.columns.client_name
                columns.studio_name = this.columns.studio_name
                columns.pic_initials = this.columns.pic_initials
                columns.pm_initials = this.columns.pm_initials
                columns.status_label = this.columns.status_label
                columns.active_phase = this.columns.active_phase
                columns.hours = this.columns.hours
                columns.computed_fee = this.columns.computed_fee
                columns.computed_spent = this.columns.computed_spent
            }

            return columns
        },
        available_project_status () {
            return this.dictionary(this.$root.constants.dictionaries.PROJECT_STATUS, "name").filter((term) => {
                if(term.code_constant.match(/^(active|on-hold|completed|dead)$/i)) return true
                return false
            })
        }
    },
    data () {
        return {
            addl_export_fields: [
                "number", "computed_fee", "hours", "computed_spent", "target_lpd", "lighting_costs", "controls_costs",
                "package", "consider_for_marketing", "pro_bono", "activated_at_as_string", "completed_at_as_string",
                "marketing_sheets_created", "marketing_photos", "web_published", "active_phase_constant", "project_sustainability_programs_label"
            ]
        }
    }
}
</script>
